import React, { useState, useEffect } from 'react';
import {
  EnvelopeIcon,
  DocumentTextIcon,
  EyeIcon,
  CheckCircleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { configAPI } from '../services/api';

const EmailTemplateSelector = ({ 
  selectedTemplate, 
  onTemplateSelect,
  required = false 
}) => {
  const [templates, setTemplates] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await configAPI.getAll();
      const emailTemplates = response.data.email_templates || {};
      
      const templateOptions = Object.keys(emailTemplates).map(key => ({
        key,
        name: key.replace('email_template_', '').replace(/_/g, ' '),
        label: emailTemplates[key].description || key,
        value: emailTemplates[key].value,
        isDefault: key === 'email_template_success_basic'
      }));

      setTemplates(templateOptions);
      
      // Se não há template selecionado, seleciona o padrão
      if (!selectedTemplate && templateOptions.length > 0) {
        const defaultTemplate = templateOptions.find(t => t.isDefault) || templateOptions[0];
        onTemplateSelect(defaultTemplate.key);
      }
    } catch (error) {
      console.error('Erro ao buscar templates de email:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setIsOpen(false);
    onTemplateSelect(template.key);
  };

  const handlePreview = async (e, template) => {
    e.stopPropagation();
    setPreview(template);
    setShowPreview(true);
  };

  const getCurrentTemplate = () => {
    return templates.find(t => t.key === selectedTemplate);
  };

  const getTemplateIcon = (templateKey) => {
    if (templateKey.includes('detailed')) return '🎨';
    if (templateKey.includes('error')) return '⚠️';
    if (templateKey.includes('minimal')) return '📝';
    return '✉️';
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Template de Email
        </label>
        <div className="animate-pulse bg-gray-200 h-10 rounded-md"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Template de Email
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
            <EnvelopeIcon className="h-3 w-3 mr-1" />
            <span>Templates Globais</span>
          </div>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="relative w-full bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <span className="flex items-center">
              {getCurrentTemplate() && (
                <>
                  <span className="mr-2">{getTemplateIcon(getCurrentTemplate().key)}</span>
                  <span className="block truncate">{getCurrentTemplate().label}</span>
                </>
              )}
              {!getCurrentTemplate() && <span className="text-gray-400">Selecionar template...</span>}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </span>
          </button>

          {isOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
              {templates.map((template) => (
                <div
                  key={template.key}
                  onClick={() => handleTemplateSelect(template)}
                  className="cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-50 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2 text-lg">{getTemplateIcon(template.key)}</span>
                        <span className="block truncate font-medium">
                          {template.label}
                        </span>
                        {template.isDefault && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Padrão
                          </span>
                        )}
                      </div>
                      <div className="flex items-center mt-1">
                        <DocumentTextIcon className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500 capitalize">{template.name}</span>
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      onClick={(e) => handlePreview(e, template)}
                      className="ml-3 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Visualizar template"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {selectedTemplate === template.key && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                    </span>
                  )}
                </div>
              ))}
              
              {templates.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  Nenhum template de email encontrado nas configurações globais.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Template Info */}
        {getCurrentTemplate() && (
          <div className="mt-2 p-3 bg-blue-50 rounded-md">
            <div className="flex items-start">
              <EnvelopeIcon className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-blue-800 font-medium">Template selecionado</p>
                <p className="text-blue-600 mt-1">{getCurrentTemplate().label}</p>
                <button
                  type="button"
                  onClick={(e) => handlePreview(e, getCurrentTemplate())}
                  className="mt-2 text-xs text-blue-700 hover:text-blue-900 underline"
                >
                  Visualizar template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && preview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowPreview(false)}></div>
            
            <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Preview: {preview.label}
                </h3>
                <button 
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                {preview.value ? (
                  <iframe
                    srcDoc={preview.value}
                    className="w-full h-96"
                    title="Template Preview"
                  />
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p>Preview não disponível</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="btn-secondary"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailTemplateSelector;